import { useCallback, useEffect, useMemo, useState } from 'react';

/** Ключ sessionStorage выбора: свой у каждого пользователя, чтобы выбор не перетекал между аккаунтами. */
export function selectionStorageKey() {
  let owner = '';
  try {
    owner = window.localStorage.getItem('auth_email') || '';
  } catch {
    owner = '';
  }
  return `violations:selection:${owner || 'anonymous'}`;
}

const EMPTY = { mode: 'ids', ids: [], types: {}, filters: null, total: 0, extras: {} };

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const isValidId = (id) =>
  (typeof id === 'number' && Number.isFinite(id)) || (typeof id === 'string' && id !== '');

function isPlainEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const keysA = Object.keys(a).filter((k) => a[k] !== undefined);
  const keysB = Object.keys(b).filter((k) => b[k] !== undefined);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((k) => isPlainEqual(a[k], b[k]));
}

function readSnapshot(storageKey) {
  const empty = { ...EMPTY, key: storageKey };
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return empty;
    const filtersOk = parsed.mode === 'filters'
      && parsed.filters && typeof parsed.filters === 'object'
      && Number.isFinite(parsed.total);
    return {
      key: storageKey,
      mode: filtersOk ? 'filters' : 'ids',
      ids: Array.isArray(parsed.ids) ? Array.from(new Set(parsed.ids.filter(isValidId))) : [],
      types: isPlainObject(parsed.types) ? parsed.types : {},
      filters: filtersOk ? parsed.filters : null,
      total: filtersOk ? parsed.total : 0,
      extras: isPlainObject(parsed.extras) ? parsed.extras : {},
    };
  } catch {
    return empty;
  }
}

function writeSnapshot(storageKey, { mode, ids, types, filters, total, extras }) {
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify({ mode, ids, types, filters, total, extras }));
  } catch {
    // хранилище недоступно или переполнено — выбор живёт только в памяти
  }
}

/** Тип события у отмеченных id: «Посчитать» и «Дубли» делят выбор по категориям. */
const withTypes = (types, ids, typeById, checked) => {
  const next = { ...types };
  ids.forEach((id) => {
    if (!checked) delete next[id];
    else if (typeById?.[id]) next[id] = typeById[id];
  });
  return next;
};

/**
 * Модель выбора событий. Режим 'ids' накапливает id между страницами без лимита,
 * режим 'filters' хранит снимок фильтра и счётчик total от backend (id не материализуются).
 * Всё, включая extras (состояние панели статистики), хранится в sessionStorage.
 */
export default function useEventSelection({ storageKey }) {
  const [stored, setState] = useState(() => readSnapshot(storageKey));

  // Смена ключа (другой пользователь) без перемонтирования: читаем снимок нового ключа
  // и никогда не пишем состояние одного ключа под другим.
  let state = stored;
  if (stored.key !== storageKey) {
    state = readSnapshot(storageKey);
    setState(state);
  }

  useEffect(() => {
    if (state.key !== storageKey) return;
    writeSnapshot(storageKey, state);
  }, [storageKey, state]);

  const idSet = useMemo(() => new Set(state.ids), [state.ids]);

  const togglePage = useCallback((pageIds, checked, typeById) => {
    setState((prev) => {
      if (prev.mode === 'filters') return prev;
      const next = new Set(prev.ids);
      pageIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      if (next.size === prev.ids.length) return prev;
      return { ...prev, ids: Array.from(next), types: withTypes(prev.types, pageIds, typeById, checked) };
    });
  }, []);

  const toggleOne = useCallback((id, type) => {
    setState((prev) => {
      if (prev.mode === 'filters') return prev;
      const next = new Set(prev.ids);
      const checked = !next.has(id);
      if (checked) next.add(id);
      else next.delete(id);
      return {
        ...prev,
        ids: Array.from(next),
        types: withTypes(prev.types, [id], type ? { [id]: type } : null, checked),
      };
    });
  }, []);

  const selectByFilter = useCallback((filters, total) => {
    setState((prev) => ({
      ...prev,
      mode: 'filters',
      filters,
      total: Number.isFinite(total) ? total : 0,
      extras: {},
    }));
  }, []);

  const clear = useCallback(() => {
    setState((prev) => ({ ...EMPTY, key: prev.key }));
  }, []);

  const syncFilters = useCallback((currentFilters) => {
    setState((prev) => {
      if (prev.mode !== 'filters') return prev;
      if (isPlainEqual(prev.filters, currentFilters)) return prev;
      return { ...prev, mode: 'ids', filters: null, total: 0, extras: {} };
    });
  }, []);

  const setExtras = useCallback((patch) => {
    setState((prev) => ({ ...prev, extras: { ...prev.extras, ...patch } }));
  }, []);

  const isSelected = useCallback(
    (id) => state.mode === 'filters' || idSet.has(id),
    [state.mode, idSet]
  );

  const pageState = useCallback((pageIds) => {
    if (!pageIds || pageIds.length === 0) return 'none';
    if (state.mode === 'filters') return 'all';
    const selected = pageIds.filter((id) => idSet.has(id)).length;
    if (selected === 0) return 'none';
    return selected === pageIds.length ? 'all' : 'some';
  }, [state.mode, idSet]);

  return {
    mode: state.mode,
    ids: idSet,
    types: state.types || {},
    filters: state.filters,
    count: state.mode === 'filters' ? state.total : idSet.size,
    extras: state.extras,
    togglePage,
    toggleOne,
    selectByFilter,
    clear,
    syncFilters,
    setExtras,
    isSelected,
    pageState,
  };
}
